import Close from '@/SVG/Close'
import NextArrow from '@/SVG/NextArrow'
import PreviousArrow from '@/SVG/PreviousArrow'
import Button from '@/components/Button'
import ChapterCard from '@/components/Chapter/ChapterCard'
import { useAudio } from '@/context/AudioContext'
import { useChapter } from '@/context/ChapterContext'
import { useNavigation } from '@/context/NavigationContext'
import { useVideo } from '@/context/VideoContext'
import chapters from '@/data/videos'
import { useEffect, useState } from 'react'
import ChapterScroll from './ChapterScroll'

export default function ChapterMenu() {
  const { showChapterMenu, currentChapterNumber, setCurrentChapter, setShowChapterMenu } = useChapter()
  const { goToLanguageSelector } = useNavigation()
  const { setVolume, setMuted } = useAudio()
  const { setShowHotspots } = useChapter()
  const { play, pause, setCurrentVideo, position: videoPosition, duration: videoDuration } = useVideo()

  // Local state for menu navigation (separate from actual chapter playback)
  const [selectedChapter, setSelectedChapter] = useState(currentChapterNumber)

  // Sync selected chapter when menu opens or current chapter changes
  useEffect(() => {
    if (showChapterMenu) {
      setSelectedChapter(currentChapterNumber)
    }
  }, [showChapterMenu, currentChapterNumber])

  // Handle card selection: set chapter and close menu (play will be triggered by menu close)
  const handleCardSelect = (chapterNumber: number) => {
    setCurrentChapter(chapterNumber) // This will load the chapter
    setShowChapterMenu(false) // This will call play()
  }

  // Handle close with chapter update
  const handleClose = () => {
    // Check if we're already on the selected chapter
    if (selectedChapter === currentChapterNumber) {
      // If we are at the end of the chapter and the chapter has hotspots,
      // instead of resuming the chapter (because its finished), we should
      // transition to the hotspots audio.
      // We should do this if we are within 1 second of the end
      if (videoPosition >= videoDuration - 1 && chapters[selectedChapter - 1]?.hasHotspots) {
        setShowChapterMenu(false, false)
        setShowHotspots(true)
        return
      }
    } else {
      // Different chapter selected, switch to it
      setCurrentChapter(selectedChapter) // Will auto-play when menu closes
    }

    setShowChapterMenu(false) // This will call play()
  }

  const handleHome = () => {
    // Reset chapter selection
    setCurrentChapter(0)
    // Hide the chapter menu
    setShowChapterMenu(false)

    // Properly handle media resources
    pause() // Stop video playback
    setShowHotspots(false) // Hide any hotspots

    // Instead of completely stopping audio, we should let the AudioContext
    // transition to the silence track through its own logic
    // The silence track will play automatically when there's no active chapter
    setCurrentVideo(null) // Clear current video, which will trigger silence audio

    // Ensure volume and mute state are at reasonable defaults
    setVolume(1.0)
    setMuted(false)

    // Use navigation context to go to language selector screen
    goToLanguageSelector()
  }

  // Scrolling handled inside ChapterScroll

  return (
    showChapterMenu && (
      <dialog
        open
        className={'fixed inset-0 z-50 bg-black bg-opacity-80 m-0 p-0 w-full h-full max-w-none max-h-none'}
        aria-labelledby="chapter-menu-title"
      >
        <nav className={'absolute top-0 left-0 right-0 flex items-center  h-[66px] bg-black z-50'}>
          <section className={'container mx-auto flex justify-between px-4  items-center'}>
            <button type={'button'} onClick={handleClose} className={'cursor-pointer'}>
              <Close />
              <span className="sr-only">Close</span>
            </button>

            <Button small onClick={handleHome}>
              Back home
            </Button>
          </section>
        </nav>

        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
          {/* Chapter cards container */}
          <ChapterScroll selectedChapter={selectedChapter}>
            {chapters.map((chapter, index) => (
              <button
                type="button"
                key={chapter.id}
                className="flex-shrink-0 p-0 mx-2 bg-transparent border-0 cursor-pointer md:mx-4"
                data-chapter={index + 1}
                onClick={() => handleCardSelect(index + 1)}
                aria-label={`Select ${chapter.title}`}
              >
                <ChapterCard chapterNumber={index + 1} active={selectedChapter - 1 === index} />
              </button>
            ))}
          </ChapterScroll>

          {/* Chapter title */}
          <h2 id="chapter-menu-title" className="mt-6 mb-8 text-xl text-white md:text-2xl">
            {chapters[selectedChapter - 1]?.title || 'Select a chapter'}
          </h2>

          {/* Navigation controls */}
          <div className="flex mb-10 space-x-6">
            <Button
              small
              onClick={() => {
                const prev = Math.max(1, selectedChapter - 1)
                setSelectedChapter(prev) // Only update local state
              }}
            >
              <PreviousArrow />
            </Button>
            <Button
              small
              onClick={() => {
                const next = Math.min(chapters.length, selectedChapter + 1)
                setSelectedChapter(next) // Only update local state
              }}
            >
              <NextArrow />
            </Button>
          </div>
        </div>
      </dialog>
    )
  )
}

export { ChapterMenu }
